import React, { useEffect, useState, useCallback } from "react";
import { normalizeDate_time } from "../../utilities";
import { useParams } from "react-router-dom";
import { selectUser } from "../../features/userSlice";
import { useSelector } from "react-redux";
import WbSunnyRoundedIcon from "@mui/icons-material/WbSunnyRounded";
import CloudOffRoundedIcon from "@mui/icons-material/CloudOffRounded";
import { PageHeader, PageShell } from "../../components/UI/Page";
import { EmptyState, LoadingState } from "../../components/UI/States";
import renderWeatherIcon from "../../components/Other/GetWeatherIcon";

const weekday = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

const capitalize = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : "");

const Weather = () => {
  const { branchlocation } = useParams();
  const user = useSelector(selectUser);

  const [weather, setWeather] = useState(null);
  const [status, setStatus] = useState("loading"); // loading | ok | error

  const branch = branchlocation || user?.branch;

  const getWeather = useCallback(async () => {
    if (!branch) {
      setStatus("error");
      return;
    }
    try {
      const res = await fetch(`/api/weather/getWeatherData?branch=${branch}`);
      const data = await res.json();
      const record = Array.isArray(data) ? data[0] : null;
      if (record && record.current) {
        setWeather(record);
        setStatus("ok");
      } else {
        setStatus((prev) => (prev === "ok" ? "ok" : "error"));
      }
    } catch (e) {
      setStatus((prev) => (prev === "ok" ? "ok" : "error"));
    }
  }, [branch]);

  useEffect(() => {
    getWeather();
    const interval = setInterval(getWeather, 60000);
    return () => clearInterval(interval);
  }, [getWeather]);

  // The /dashboard/weather/display/:branchlocation route renders with no
  // header on branch TVs — big type, no page chrome, readable from across
  // the room.
  const isDisplay = Boolean(branchlocation);

  // Signed-in page: themed canvas + standard page column. TV display: its
  // own full-screen surface. A plain render helper (not a component) so the
  // 60-second refresh never remounts the page.
  const frame = (children) =>
    isDisplay ? (
      <div className="app-page min-h-screen">
        <div className="mx-auto w-full max-w-[110rem] px-6 py-8 lg:px-12 lg:py-12">
          {children}
        </div>
      </div>
    ) : (
      <div className="app-canvas min-h-[calc(100vh-140px)] w-full">
        <PageShell size="lg">
          <PageHeader
            icon={WbSunnyRoundedIcon}
            tone="sky"
            eyebrow="Everyday Tools"
            title="Weather"
            description="Current conditions and the week ahead for your branch. Refreshes every minute."
          />
          {children}
        </PageShell>
      </div>
    );

  if (status === "loading") {
    return frame(<LoadingState label="Loading weather…" />);
  }

  if (status === "error" || !weather) {
    return frame(
      <div className="app-card rounded-2xl border">
        <EmptyState
          icon={CloudOffRoundedIcon}
          title="Weather unavailable"
          description={
            branch
              ? `We couldn't load current conditions for ${capitalize(branch)}.`
              : "No branch selected for weather."
          }
        />
      </div>
    );
  }

  const current = weather.current;
  const hourly = (weather.hourly || []).slice(1, 7);
  const daily = (weather.daily || []).slice(1, 6);

  // Flag data that hasn't refreshed in a while (cron may be down).
  const ageHours = weather.lastUpdated
    ? (Date.now() - new Date(weather.lastUpdated).getTime()) / 3600000
    : null;
  const isStale = ageHours !== null && ageHours > 3;

  // The animated icons carry a lot of built-in padding, so each sits in a
  // fixed box that its SVG fills (instead of the icon's own size class).
  const iconBox = "block shrink-0 [&>svg]:h-full [&>svg]:w-full";

  return frame(
    <div className={isDisplay ? "space-y-8" : "space-y-5"}>
      {/* Current conditions */}
      <section
        className={`app-card relative isolate rounded-[1.75rem] border ${
          isDisplay ? "p-8 lg:p-12" : "p-6 sm:p-8"
        }`}
      >
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 -z-10 overflow-hidden rounded-[inherit]"
        >
          <div className="absolute inset-0 bg-gradient-to-br from-sky-400/20 via-sky-300/[0.08] to-transparent dark:from-sky-500/[0.18] dark:via-indigo-500/10" />
          <div className="absolute -right-20 -top-24 h-72 w-72 rounded-full bg-amber-300/25 blur-3xl dark:bg-sky-400/15" />
        </div>
        <div className="flex flex-col gap-6 sm:flex-row sm:items-center sm:justify-between">
          <div className="min-w-0">
            <p className={`app-eyebrow ${isDisplay ? "text-base" : ""}`}>
              Current conditions
            </p>
            <h2
              className={`mt-1 font-bold tracking-tight ${
                isDisplay ? "text-6xl lg:text-7xl" : "text-3xl sm:text-4xl"
              }`}
            >
              {capitalize(weather.branch)}
            </h2>
            <p
              className={`app-muted mt-2 ${
                isDisplay ? "text-3xl" : "text-base"
              }`}
            >
              {capitalize(current.weather?.[0]?.description)}
            </p>
          </div>
          <div className="flex items-center gap-2 sm:gap-4">
            <span
              className={`${iconBox} ${
                isDisplay ? "-my-8 h-64 w-64" : "-my-6 -ml-4 h-28 w-28 sm:h-32 sm:w-32"
              }`}
            >
              {renderWeatherIcon(
                current.weather?.[0]?.main,
                64,
                current.weather?.[0]?.description
              )}
            </span>
            <div>
              <p
                className={`font-semibold leading-none tabular-nums tracking-tighter ${
                  isDisplay ? "text-[9rem]" : "text-5xl sm:text-6xl"
                }`}
              >
                {Math.round(current.temp)}&deg;F
              </p>
              <p
                className={`app-muted mt-2 tabular-nums ${
                  isDisplay ? "text-3xl" : "text-sm"
                }`}
              >
                Feels like {Math.round(current.feels_like)}&deg;F
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Hourly forecast */}
      {hourly.length > 0 && (
        <section
          className={`app-card rounded-2xl border ${
            isDisplay ? "p-8" : "p-5 sm:p-6"
          }`}
        >
          <h2
            className={`font-semibold tracking-tight ${
              isDisplay ? "text-3xl" : "text-lg"
            }`}
          >
            Hourly Forecast
          </h2>
          <div
            className={`mt-4 flex gap-3 overflow-x-auto pb-1 ${
              isDisplay ? "justify-between gap-5" : ""
            }`}
          >
            {hourly.map((hour, i) => (
              <div
                key={i}
                className={`app-surface flex flex-1 flex-col items-center rounded-2xl border ${
                  isDisplay ? "min-w-[11rem] p-5" : "min-w-[6.5rem] p-3"
                }`}
              >
                <p
                  className={`app-muted font-medium tabular-nums ${
                    isDisplay ? "text-2xl" : "text-xs"
                  }`}
                >
                  {normalizeDate_time(hour.dt * 1000)}
                </p>
                <span
                  className={`${iconBox} ${
                    isDisplay ? "h-32 w-32" : "-my-1 h-16 w-16"
                  }`}
                >
                  {renderWeatherIcon(
                    hour.weather?.[0]?.main,
                    36,
                    hour.weather?.[0]?.description
                  )}
                </span>
                <p
                  className={`font-semibold tabular-nums ${
                    isDisplay ? "text-5xl" : "text-lg"
                  }`}
                >
                  {Math.round(hour.temp)}&deg;
                </p>
                <p
                  className={`app-muted text-center ${
                    isDisplay ? "mt-1 text-xl" : "text-xs"
                  }`}
                >
                  {hour.weather?.[0]?.main}
                </p>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* 5-day forecast */}
      {daily.length > 0 && (
        <section
          className={`app-card rounded-2xl border ${
            isDisplay ? "p-8" : "p-5 sm:p-6"
          }`}
        >
          <h2
            className={`font-semibold tracking-tight ${
              isDisplay ? "text-3xl" : "text-lg"
            }`}
          >
            5-Day Forecast
          </h2>
          <div
            className={`mt-4 grid gap-3 ${
              isDisplay
                ? "grid-cols-5 gap-5"
                : "grid-cols-2 sm:grid-cols-3 lg:grid-cols-5"
            }`}
          >
            {daily.map((day, i) => (
              <div
                key={i}
                className={`app-surface flex flex-col items-center rounded-2xl border ${
                  isDisplay ? "p-6" : "p-3"
                }`}
              >
                <p
                  className={`font-semibold ${
                    isDisplay ? "text-3xl" : "text-sm"
                  }`}
                >
                  {weekday[new Date(day.dt * 1000).getDay()]}
                </p>
                <span
                  className={`${iconBox} ${
                    isDisplay ? "h-36 w-36" : "-my-1 h-[4.5rem] w-[4.5rem]"
                  }`}
                >
                  {renderWeatherIcon(
                    day.weather?.[0]?.main,
                    40,
                    day.weather?.[0]?.description
                  )}
                </span>
                <p
                  className={`app-muted text-center ${
                    isDisplay ? "text-xl" : "text-xs"
                  }`}
                >
                  {day.weather?.[0]?.main}
                </p>
                <div
                  className={`mt-2 flex gap-1.5 ${isDisplay ? "mt-4 gap-3" : ""}`}
                >
                  <span
                    className={`rounded-full bg-orange-500/15 font-semibold tabular-nums text-orange-700 dark:text-orange-300 ${
                      isDisplay ? "px-4 py-1 text-2xl" : "px-2 py-0.5 text-xs"
                    }`}
                  >
                    {Math.round(day.temp.max)}&deg;
                  </span>
                  <span
                    className={`rounded-full bg-sky-500/15 font-semibold tabular-nums text-sky-700 dark:text-sky-300 ${
                      isDisplay ? "px-4 py-1 text-2xl" : "px-2 py-0.5 text-xs"
                    }`}
                  >
                    {Math.round(day.temp.min)}&deg;
                  </span>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      <p
        className={`app-muted text-center ${isDisplay ? "text-lg" : "text-xs"}`}
      >
        {weather.lastUpdated
          ? `Last updated ${normalizeDate_time(weather.lastUpdated)} EST`
          : ""}
        {isStale && (
          <span className="ml-2 font-medium text-amber-700 dark:text-amber-300">
            · data may be out of date
          </span>
        )}
      </p>
    </div>
  );
};

export default Weather;
